import type { Role } from "@so/shared";
import { NavLink, Outlet } from "react-router-dom";
import { useMe } from "../api/queries.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { Button, cx, Loading } from "./ui.js";

const ROLE_LABEL: Record<Role, string> = {
  patient: "Patient",
  clinician: "Specialist",
  admin: "Care coordinator",
};

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly roles: readonly Role[];
}

/**
 * Navigation is derived from the same role table the server enforces, so the
 * app never offers an action that would come back refused. The server still
 * checks every request - this only avoids showing people doors they cannot
 * open.
 */
const NAV: readonly NavItem[] = [
  { to: "/consultations", label: "My consultations", roles: ["patient", "clinician", "admin"] },
  { to: "/specialists", label: "Find a specialist", roles: ["patient", "admin"] },
  { to: "/availability", label: "My calendar", roles: ["clinician"] },
  { to: "/queue", label: "Coordination queue", roles: ["admin"] },
  { to: "/staff", label: "Staff", roles: ["admin"] },
];

function Logo() {
  return (
    <span className="flex items-center gap-2.5">
      <svg viewBox="0 0 32 32" className="h-7 w-7" aria-hidden="true">
        <rect width="32" height="32" rx="7" className="fill-accent" />
        <path
          d="M9 16.5h4l2-5 2.5 9 2-4h3.5"
          stroke="white"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="text-[16.5px] font-semibold tracking-[-0.01em]">ReassureMed</span>
    </span>
  );
}

export function Layout() {
  const { data: me, isPending } = useMe();
  const { signOut } = useAuth();

  if (isPending) return <Loading label="Signing you in" />;

  const roles = me?.roles ?? [];
  const visible = NAV.filter((item) => item.roles.some((role) => roles.includes(role)));
  const primaryRole = roles[0];

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only-focusable bg-accent absolute top-2 left-2 z-50 rounded px-3 py-2 text-white"
      >
        Skip to content
      </a>

      <header className="border-line bg-surface border-b">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3">
          <Logo />

          <nav className="flex flex-1 flex-wrap gap-1" aria-label="Main">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    "rounded-md px-3 py-1.5 text-[14.5px] transition-colors",
                    isActive
                      ? "bg-accent-soft text-accent font-medium"
                      : "text-ink-soft hover:bg-surface-2 hover:text-ink",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              <p className="text-[14px] font-medium">{me?.name}</p>
              {primaryRole !== undefined && (
                <p className="text-ink-faint text-[12px]">{ROLE_LABEL[primaryRole]}</p>
              )}
            </div>
            <Button variant="ghost" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-5xl px-5 py-9">
        <Outlet />
      </main>

      <footer className="text-ink-faint mx-auto max-w-5xl px-5 pb-10 text-[12.5px]">
        Demonstration environment. All patient information shown here is synthetic.
      </footer>
    </div>
  );
}
